import { useState, useRef, useEffect, useCallback } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { tokens } from "../tokens";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { Bell, MessageCircle } from "lucide-react";
// ⚠️  Adjust this path to match where YOUR supabase client lives.
// Common locations: '../lib/supabase' | '../utils/supabaseClient' | '../supabase'
import { supabase } from "../lib/supabase";
import Logo from "../components/AppLogo";

/* ─── Font injection — matches the rest of JiraniApp: Instrument Serif + DM Sans ─── */
if (!document.getElementById("__jf")) {
  const l = document.createElement("link");
  l.id = "__jf";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ─── Design tokens — aligned to Footer.jsx / JiraniPro landing page ─── */
const T = {
  green:      "#1e9254",
  greenDark:  "#155c36",
  greenLight: "#2ab76b",
  greenTint:  "#eef8f3",
  greenBd:    "#a7d9bc",
  greenGlow:  "rgba(30,146,84,0.14)",
  deepGreen:  "#0d2e1a",
  darkBorder: "#1e4a2e",

  pageBg:     "#f7f8fa",
  white:      "#FFFFFF",
  card:       "#FFFFFF",
  card2:      "#f7f8fa",
  border:     "#e4e7ef",
  borderFaint:"#edf0f4",

  text:       "#0f1117",
  textMid:    "#4a5268",
  sub:        "#6b7280",
  faint:      "#9aa1ae",
  ghost:      "#cfd3db",

  amber:      "#b5720f",
  amberTint:  "#fdf3e2",
  blue:       "#1d4ed8",
  blueTint:   "#eff6ff",
  red:        "#c53030",
  redTint:    "#fef2f2",

  sans:  "'DM Sans', -apple-system, sans-serif",
  serif: "'Instrument Serif', Georgia, serif",
  font:  "'DM Sans', -apple-system, sans-serif",

  r4:"4px", r8:"8px", r10:"10px", r12:"12px",
  r14:"14px", r16:"16px", r20:"20px", rFull:"999px",

  s0: "none",
  s1: "0 1px 3px rgba(15,17,23,0.04)",
  s2: "0 2px 10px rgba(15,17,23,0.05)",
  s3: "0 16px 36px -12px rgba(15,17,23,0.16)",
  sInner: "inset 0 1px 3px rgba(15,17,23,0.05)",
  sGreen: "0 4px 16px rgba(30,146,84,0.22)",
  tx: "0.18s cubic-bezier(0.2,0,0,1)",
};

/* ─── Shared card style helpers ─── */
const cardSx = (h, isMobile) => ({
  background: T.card,
  borderRadius: isMobile ? T.r12 : T.r16,
  border: `1px solid ${!isMobile && h ? T.greenBd : T.border}`,
  boxShadow: isMobile ? T.s1 : (h ? T.s3 : T.s1),
  transition: isMobile ? "none" : "box-shadow 0.22s ease-out, border-color 0.22s",
});
const labelSx = {
  margin: 0,
  fontSize: 10,
  fontWeight: 700,
  color: T.faint,
  fontFamily: T.sans,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};
const numSx = {
  fontSize: 26,
  fontWeight: 700,
  color: T.text,
  fontFamily: T.sans,
  letterSpacing: "-0.03em",
  lineHeight: 1,
};
const trendSx = (up) => ({
  fontSize: 11,
  fontWeight: 600,
  color: up ? T.greenDark : T.red,
  background: up ? T.greenTint : T.redTint,
  borderRadius: T.rFull,
  padding: "2px 8px",
  fontFamily: T.sans,
});
const iconBoxSx = (bg) => ({
  width: 34,
  height: 34,
  borderRadius: T.r10,
  background: bg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

/* ─── Status metadata — kept semantic (blue/green/red) but softened to fit the flatter palette ─── */
const STATUS_META = {
  Upcoming:     { bg:"#eff6ff", color:"#1d4ed8", dot:"#3b82f6", bd:"#bfdbfe" },
  "In Progress":{ bg: T.greenTint, color: T.greenDark, dot: T.green, bd: T.greenBd },
  Completed:    { bg: T.greenTint, color: T.greenDark, dot: T.greenLight, bd: T.greenBd },
  Cancelled:    { bg:"#fef2f2", color:"#991b1b", dot:"#ef4444", bd:"#fecaca" },
};

/* ─── Map raw Supabase booking to UI booking shape ─── */
function mapBooking(b) {
  const statusMap = {
    pending:         "Upcoming",
    pending_payment: "Upcoming",
    confirmed:       "Upcoming",
    in_progress:     "In Progress",
    completed:       "Completed",
    cancelled:       "Cancelled",
  };
  const name     = b.pro_profiles?.profiles?.full_name || "JiraniPro";
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const colors   = [T.green, "#0369A1", "#7C3AED", T.amber, "#BE185D", T.textMid, "#0E7490", T.greenDark];
  const color    = colors[Math.abs(name.charCodeAt(0) + name.charCodeAt(1)) % colors.length];

  return {
    id:        b.id,
    pro: { name, initials, color, service: b.description || "Service" },
    status:    statusMap[b.status] || "Upcoming",
    rawStatus: b.status,
    date:      new Date(b.scheduled_time).toLocaleDateString("en-KE", { month:"short", day:"numeric" }),
    time:      new Date(b.scheduled_time).toLocaleTimeString("en-KE", { hour:"2-digit", minute:"2-digit" }),
    location:  b.location || "Nairobi",
    price:     b.estimated_price || 0,
    rating:    b.reviews?.[0]?.rating ?? null,
    reviewed:  !!(b.reviews?.[0]?.rating),
    professionalId: b.pro_id || null,
    conversationId: null,
  };
}

/* ─── Map Supabase saved pro to UI shape ─── */
function mapSavedPro(s) {
  const name     = s.pro_profiles?.profiles?.full_name || "JiraniPro";
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const colors   = [T.green, "#0369A1", "#7C3AED", T.amber, "#BE185D", T.textMid, "#0E7490", T.greenDark];
  const color    = colors[Math.abs(name.charCodeAt(0) + name.charCodeAt(1)) % colors.length];
  return {
    id:           s.professional_id,
    favoriteId:   s.id,
    pro: {
      name,
      initials,
      color,
      service: s.pro_profiles?.services?.[0]?.name || "Professional Service",
    },
    location: s.pro_profiles?.profiles?.location || "Nairobi",
    rating:   s.pro_profiles?.avg_rating ?? 0,
    reviews:  s.pro_profiles?.review_count ?? 0,
    price:    s.pro_profiles?.base_price ?? 0,
  };
}

/* ════════════════════════════════════════════════
   ATOMS
════════════════════════════════════════════════ */

/* Flat solid avatar — no gradient blend, matches Footer/landing page's flat surfaces */
function Ava({ pro, size = 52 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: pro.color,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.32), fontWeight: 700,
      color: "#fff", fontFamily: T.sans, letterSpacing: "-0.01em",
      boxShadow: `0 0 0 2px #fff`,
    }}>
      {pro.initials}
    </div>
  );
}

function Pill({ status }) {
  const m = STATUS_META[status] || STATUS_META.Upcoming;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: m.bg, border: `1px solid ${m.bd}`,
      borderRadius: T.rFull, padding: "3px 10px", flexShrink: 0,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.dot }} />
      <span style={{ fontSize: 11, fontWeight: 600, color: m.color, fontFamily: T.sans, letterSpacing: "0.01em" }}>
        {status}
      </span>
    </span>
  );
}

function Stars({ n, size = 12, interactive = false, onRate }) {
  const [hover, setHover] = useState(0);
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1,2,3,4,5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 20 20"
          fill={i <= (interactive ? (hover || n) : n) ? "#D97706" : "#E5E7EB"}
          style={{ cursor: interactive ? "pointer" : "default", transition: "fill 0.1s" }}
          onMouseEnter={() => interactive && setHover(i)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onRate && onRate(i)}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
    </span>
  );
}

function Btn({ children, v = "ghost", size = "md", onClick, disabled, full }) {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const { isMobile } = useBreakpoint();
  const sz = {
    sm: { fs:12.5, pad:"5px 13px", h:32, r:T.r8 },
    md: { fs:13.5, pad:"9px 18px", h:38, r:T.r10 },
    lg: { fs:14.5, pad:"12px 24px", h:44, r:T.rFull },
  }[size];
  const vs = {
    primary: { bg: h ? T.greenLight : T.green, col:"#fff", bd:"none", sh: T.sGreen },
    ghost:   { bg: h ? T.borderFaint : T.white, col:T.textMid, bd:`1px solid ${T.border}`, sh: T.s0 },
    danger:  { bg: h ? T.redTint : T.white, col:T.red, bd:"1px solid #FECACA", sh: T.s0 },
    review:  { bg: h ? T.greenTint : T.white, col:T.greenDark, bd:`1px solid ${T.greenBd}`, sh: T.s0 },
    rebook:  { bg: h ? T.greenTint : T.card2, col: h ? T.greenDark : T.sub, bd:`1px solid ${h ? T.greenBd : T.border}`, sh: T.s0 },
    dark:    { bg: h ? "#123b25" : T.deepGreen, col:"#fff", bd:"none", sh: T.s0 },
    ghostDark: { bg: h ? "rgba(255,255,255,0.1)" : "transparent", col:"#fff", bd:"1.5px solid rgba(255,255,255,0.35)", sh: T.s0 },
  };
  const s = vs[v] || vs.ghost;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => { setH(false); setP(false); }}
      onMouseDown={() => setP(true)}  onMouseUp={() => setP(false)}
      style={{
        fontFamily: T.sans, fontSize: sz.fs, fontWeight: 600, cursor: disabled ? "default" : "pointer",
        padding: sz.pad, height: sz.h, borderRadius: sz.r, border: s.bd,
        background: s.bg, color: s.col, boxShadow: s.sh,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        whiteSpace: "nowrap", transition: "all 0.18s ease-out",
        transform: p ? "scale(0.97)" : "scale(1)",
        opacity: disabled ? 0.5 : 1, outline: "none", letterSpacing: "0.005em",
        width: full ? "100%" : "auto",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}>
      {children}
    </button>
  );
}

function MetaCell({ icon, label, primary, secondary, flex }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ display:"flex", gap:8, alignItems:"flex-start", ...(flex ? { flex:1, minWidth:0 } : { flexShrink:0, minWidth: isMobile ? 0 : 118 }) }}>
      <div style={{ marginTop:2, flexShrink:0, color:T.faint }}>{icon}</div>
      <div style={{ minWidth:0 }}>
        <p style={{ margin:0, fontSize:10, fontWeight:600, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</p>
        <p style={{ margin:"3px 0 0", fontSize: isMobile ? 12.5 : 13, fontWeight:600, color:T.text, fontFamily:T.sans, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{primary}</p>
        {secondary && <p style={{ margin:0, fontSize:11, fontWeight:400, color:T.sub, fontFamily:T.sans }}>{secondary}</p>}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   NAVBAR — mobile now has a real search entry point
   instead of silently dropping the search bar
════════════════════════════════════════════════ */
function Navbar({ onNav, user, unreadCount, notifCount, onLogout }) {
  const [focused,     setFocused]     = useState(false);
  const [dropOpen,    setDropOpen]    = useState(false);
  const [notifOpen,   setNotifOpen]   = useState(false);
  const dropRef  = useRef(null);
  const notifRef = useRef(null);
  const { isMobile, isTablet } = useBreakpoint();
  const navPaddingX  = isMobile ? 16 : (isTablet ? 24 : 40);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current  && !dropRef.current.contains(e.target))  setDropOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initials = user?.full_name
    ? user.full_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : (user?.email ? user.email[0].toUpperCase() : "JD");

  const dropItems = [
    { label:"Help & Support", icon:"M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.09 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16z", path:"/help" },
    { label:"Become a Pro",   icon:"M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z", path:"/pro-onboarding" },
    { label:"divider" },
    { label:"Logout",         icon:"M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9", action:"logout", danger:true },
  ];

  return (
    <header style={{
      position:"sticky", top:0, zIndex:300,
      background:"rgba(255,255,255,0.94)",
      backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
      height: isMobile ? 56 : 62,
      display:"flex", alignItems:"center",
      padding:`0 ${navPaddingX}px`,
      justifyContent:"space-between", gap: isMobile ? 10 : 24,
    }}>

      <Logo size={isMobile ? "sm" : "md"} />

      <div style={{ display:"flex", alignItems:"center", gap: isMobile ? 8 : 10, flexShrink:0 }}>

        <div style={{ position:"relative" }}>
          <TopBarIconButton IconComp={MessageCircle} tint={{ bg: T.blueTint, bd: "#bfdbfe", fg: T.blue }} badgeCount={unreadCount} onClick={() => onNav("/messages")} title="Messages" />
        </div>

        <div ref={notifRef} style={{ position:"relative" }}>
          <TopBarIconButton IconComp={Bell} tint={{ bg: T.amberTint, bd: "#f0dba9", fg: T.amber }} badgeCount={notifCount} onClick={() => { setNotifOpen(o => !o); setDropOpen(false); }} title="Notifications" />
          {notifOpen && (
            <div style={{
              position:"absolute", top:"calc(100% + 10px)", right:0,
              width: isMobile ? "calc(100vw - 32px)" : 360,
              background:T.white, borderRadius:T.r16, boxShadow:T.s3,
              border:`1px solid ${T.border}`, zIndex:400, overflow:"hidden",
            }}>
              <div style={{ padding:"14px 18px 10px", borderBottom:`1px solid ${T.borderFaint}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:14, fontWeight:600, color:T.text, fontFamily:T.sans }}>Notifications</span>
                <button onClick={() => onNav("/notifications")} style={{ fontSize:12, color:T.greenDark, fontFamily:T.sans, background:"none", border:"none", cursor:"pointer" }}>See all</button>
              </div>
              <div style={{ padding:"12px 18px 16px" }}>
                <p style={{ margin:0, fontSize:13, color:T.faint, fontFamily:T.sans, textAlign:"center", padding:"16px 0" }}>
                  {notifCount > 0 ? `${notifCount} unread notification${notifCount > 1 ? "s" : ""}` : "You're all caught up"}
                </p>
              </div>
              <div style={{ padding:"0 18px 14px" }}>
                <Btn v="ghost" size="sm" full onClick={() => { onNav("/notifications"); setNotifOpen(false); }}>
                  View all notifications
                </Btn>
              </div>
            </div>
          )}
        </div>

        <div ref={dropRef} style={{ position:"relative" }}>
          <div
            onClick={() => { setDropOpen(o => !o); setNotifOpen(false); }}
            style={{
              display:"flex", alignItems:"center", gap: isMobile ? 4 : 6,
              cursor:"pointer", padding: 2,
              borderRadius: 999,
              boxShadow: `0 0 0 2px ${dropOpen ? T.green : T.greenBd}`,
              transition: T.tx,
            }}
          >
            <div style={{
              width: isMobile ? 26 : 32, height: isMobile ? 26 : 32, borderRadius: "50%",
              background: `linear-gradient(145deg, ${T.green}22, ${T.greenTint})`,
              border: `2px solid ${T.white}`, boxShadow: "0 1px 4px rgba(30,146,84,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
            }}>
              <span style={{ fontSize: isMobile ? 10 : 12.5, fontWeight: 700, color: T.greenDark, fontFamily: T.sans }}>{initials}</span>
              <span style={{ position: "absolute", bottom: -1, right: -1, width: isMobile ? 7 : 9, height: isMobile ? 7 : 9, borderRadius: "50%", background: T.green, border: "2px solid #fff" }} />
            </div>
            {!isMobile && (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.faint} strokeWidth="2" style={{ transform: dropOpen ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.18s" }}>
                <path d="M6 9l6 6 6-6"/>
              </svg>
            )}
          </div>

          {dropOpen && (
            <div style={{
              position:"absolute", top:"calc(100% + 10px)", right:0,
              width:220, background:T.white, borderRadius:T.r16,
              boxShadow:T.s3, border:`1px solid ${T.border}`, zIndex:400,
              overflow:"hidden",
            }}>
              <div style={{ padding:"14px 16px 12px", borderBottom:`1px solid ${T.borderFaint}` }}>
                <p style={{ margin:0, fontSize:13, fontWeight:600, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {user?.full_name || user?.email || "My Account"}
                </p>
                <p style={{ margin:"2px 0 0", fontSize:11, color:T.faint, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {user?.email || ""}
                </p>
              </div>
              <div style={{ padding:"6px 0" }}>
                {dropItems.map((item, idx) => {
                  if (item.label === "divider") return <div key={idx} style={{ height:1, background:T.borderFaint, margin:"4px 0" }}/>;
                  return (
                    <DropItem
                      key={item.label}
                      item={item}
                      onNav={onNav}
                      onLogout={onLogout}
                      onClose={() => setDropOpen(false)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function NavIconBtn({ children, badge, badgeCount, onClick }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={onClick} style={{
      width: isMobile ? 34 : 36, height: isMobile ? 34 : 36, borderRadius:T.r10,
      cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
      background: h ? T.borderFaint : "transparent", position:"relative",
      transition:"background 0.18s", minHeight: isMobile ? tokens.spacing[44] : "auto",
    }}>
      {children}
      {badge && (
        <span style={{
          position:"absolute", top:6, right:6,
          minWidth:7, height:7, borderRadius:"50%",
          background:"#EF4444", border:"1.5px solid #fff",
        }}/>
      )}
    </div>
  );
}

/* Copied from CustomerSettingsPage.jsx's TopBarIconButton — same circular
   tinted-badge shell, same lucide icon usage, same red count badge. */
function TopBarIconButton({ IconComp, badgeCount, onClick, title, tint }) {
  const { isMobile } = useBreakpoint();
  const capped = badgeCount > 9 ? "9+" : badgeCount;
  const btnSize = isMobile ? 27 : 34;
  const iconSize = isMobile ? 12 : 14;
  return (
    <button onClick={onClick} title={title} style={{
      width: btnSize, height: btnSize, borderRadius: "50%", background: tint.bg,
      border: `1px solid ${tint.bd}`, display: "flex", alignItems: "center",
      justifyContent: "center", cursor: "pointer", transition: T.tx,
      position: "relative", padding: 0,
    }}>
      <IconComp size={iconSize} color={tint.fg} strokeWidth={2} />
      {badgeCount > 0 && (
        <span style={{
          position: "absolute", top: isMobile ? -2 : -3, right: isMobile ? -2 : -3, minWidth: isMobile ? 13 : 15, height: isMobile ? 13 : 15,
          borderRadius: "50%", background: "#EF4444", color: "#fff", fontSize: isMobile ? 8 : 9,
          fontWeight: 700, fontFamily: T.sans, display: "flex", alignItems: "center",
          justifyContent: "center", border: "1.5px solid #fff", pointerEvents: "none",
        }}>
          {capped}
        </span>
      )}
    </button>
  );
}

function DropItem({ item, onNav, onLogout, onClose }) {
  const [h, setH] = useState(false);
  const handle = () => {
    onClose();
    if (item.action === "logout") { onLogout(); return; }
    onNav(item.path);
  };
  return (
    <button
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={handle}
      style={{
        width:"100%", display:"flex", alignItems:"center", gap:10,
        padding:"8px 16px", border:"none", cursor:"pointer", fontFamily:T.sans,
        background: h ? (item.danger ? "#FEF2F2" : T.borderFaint) : "transparent",
        color: item.danger ? T.red : T.textMid,
        fontSize:13, fontWeight:400, textAlign:"left",
        transition:"background 0.15s",
      }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {item.icon.split("M").filter(Boolean).map((d, i) => <path key={i} d={"M"+d}/>)}
      </svg>
      {item.label}
    </button>
  );
}

/* ════════════════════════════════════════════════
   PAGE HEADER — compact replacement for oversized hero card
════════════════════════════════════════════════ */
function PageHeader({ onBrowse, user, kpis }) {
  const { isMobile } = useBreakpoint();
  const hasBookings = (kpis.totalBookings ?? 0) > 0;

  return (
    <div style={{
      padding: isMobile ? "4px 0" : "4px 0 8px",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      flexWrap: "wrap", gap: 16,
    }}>
      <div>
        <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.green, margin: "0 0 6px", fontFamily: T.sans }}>My bookings</p>
        {!hasBookings && (
          <h1 style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: isMobile ? 20 : 24, fontWeight: 400, color: T.text, margin: 0 }}>
            Let's get your first <span style={{ color: T.green }}>booking</span>
          </h1>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn v="primary" size={isMobile ? "sm" : "md"} onClick={onBrowse}>Browse Services</Btn>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   STATS GRID
════════════════════════════════════════════════ */
function StatsGrid({ kpis, kpiLoading }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: isMobile ? 10 : 14 }}>
      <StatBookings  kpis={kpis} loading={kpiLoading}/>
      <StatSpend     kpis={kpis} loading={kpiLoading}/>
      <StatPros      kpis={kpis} loading={kpiLoading}/>
      <StatRating    kpis={kpis} loading={kpiLoading}/>
    </div>
  );
}

function KpiSkeleton() {
  return <div style={{ height:26, width:56, borderRadius:T.r8, background:T.borderFaint, animation:"kpiPulse 1.4s ease-in-out infinite" }}/>;
}

function StatBookings({ kpis, loading }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();
  const cardPadding = isMobile ? "14px 16px" : "20px 22px";
  const numFontSize = isMobile ? 22 : 26;
  const months  = (kpis.monthlyBookings || []).slice(-7);
  const labels  = months.map(m => new Date(m.month + "-01").toLocaleDateString("en-KE", { month:"narrow" }));
  const vals    = months.map(m => m.count);
  const max     = Math.max(...vals, 1);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ ...cardSx(h, isMobile), padding:cardPadding }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div>
          <p style={labelSx}>Total bookings</p>
          <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:4, flexWrap:"wrap" }}>
            {loading ? <KpiSkeleton/> : <span style={{ ...numSx, fontSize:numFontSize }}>{kpis.totalBookings ?? 0}</span>}
            {!loading && !isMobile && kpis.bookingTrend != null && (
              <span style={trendSx(kpis.bookingTrend >= 0)}>{kpis.bookingTrend >= 0 ? "+" : ""}{kpis.bookingTrend} this month</span>
            )}
          </div>
        </div>
        <div style={iconBoxSx(T.greenTint)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.greenDark} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
        </div>
      </div>
      {vals.length > 0 ? (
        <div style={{ display:"flex", alignItems:"flex-end", gap:4, height:40 }}>
          {vals.map((v, i) => {
            const last = i === vals.length - 1;
            return (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <div style={{ width:"100%", borderRadius:"3px 3px 0 0", height:`${Math.round((v / max) * 32)}px`, background: last ? T.green : T.greenBd, transition:"height 0.4s ease" }}/>
                <span style={{ fontSize:8, fontFamily:T.sans, color: last ? T.greenDark : T.ghost, fontWeight: last ? 700 : 400 }}>{labels[i]}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ height:40, display:"flex", alignItems:"center" }}>
          <span style={{ fontSize:11, color:T.faint, fontFamily:T.sans }}>{loading ? "Loading..." : "No booking history yet"}</span>
        </div>
      )}
    </div>
  );
}

function StatSpend({ kpis, loading }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();
  const cardPadding  = isMobile ? "14px 16px" : "20px 22px";
  const numFontSize  = isMobile ? 22 : 26;
  const spent  = kpis.totalSpend   ?? 0;
  const budget = kpis.spendBudget  ?? 0;
  const pct    = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
  const monthly = (kpis.monthlySpend || []).slice(-4);
  const mLabels = monthly.map(m => new Date(m.month + "-01").toLocaleDateString("en-KE", { month:"narrow" }));
  const mVals   = monthly.map(m => m.total);
  const mMax    = Math.max(...mVals, 1);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ ...cardSx(h, isMobile), padding:cardPadding }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div>
          <p style={labelSx}>Ksh spent</p>
          <div style={{ display:"flex", alignItems:"baseline", gap:6, marginTop:4 }}>
            {loading ? <KpiSkeleton/> : <span style={{ ...numSx, fontSize:numFontSize }}>{spent > 0 ? spent.toLocaleString() : "0"}</span>}
          </div>
          <p style={{ margin:"3px 0 0", fontSize:11, color:T.faint, fontFamily:T.sans }}>
            {budget > 0
              ? `of Ksh ${budget.toLocaleString()} balance`
              : loading ? "Loading balance..." : "No wallet balance"}
          </p>
        </div>
        <div style={iconBoxSx(T.amberTint)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
        </div>
      </div>
      <div style={{ marginBottom:12 }}>
        <div style={{ height:6, borderRadius:T.rFull, background:T.borderFaint, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, borderRadius:T.rFull, background:T.green }}/>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
          <span style={{ fontSize:10, color:T.greenDark, fontFamily:T.sans, fontWeight:600 }}>{pct}% used</span>
          {budget > 0 && (
            <span style={{ fontSize:10, color:T.faint, fontFamily:T.sans }}>Ksh {Math.max(0, budget - spent).toLocaleString()} left</span>
          )}
        </div>
      </div>
      {mVals.length > 0 ? (
        <div style={{ display:"flex", alignItems:"flex-end", gap:5, height:28 }}>
          {mVals.map((v, i) => (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
              <div style={{ width:"100%", borderRadius:"2px 2px 0 0", height:`${Math.round((v / mMax) * 20)}px`, background: i === mVals.length - 1 ? T.amber : "#f0dba9" }}/>
              <span style={{ fontSize:8, color:T.faint, fontFamily:T.sans }}>{mLabels[i]}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ height:28, display:"flex", alignItems:"center" }}>
          <span style={{ fontSize:11, color:T.faint, fontFamily:T.sans }}>{loading ? "Loading..." : "No data yet"}</span>
        </div>
      )}
    </div>
  );
}

function StatPros({ kpis, loading }) {
  const [h, setH]  = useState(false);
  const { isMobile } = useBreakpoint();
  const cardPadding  = isMobile ? "14px 16px" : "20px 22px";
  const numFontSize  = isMobile ? 22 : 26;
  const prosList     = kpis.prosDetail || [];
  const cats         = kpis.prosCategories || [];
  const catMax       = Math.max(...cats.map(c => c.n), 1);
  const catColors    = [T.green, "#0369A1", "#7C3AED"];
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ ...cardSx(h, isMobile), padding:cardPadding }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
        <div>
          <p style={labelSx}>Pros hired</p>
          <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:4 }}>
            {loading ? <KpiSkeleton/> : <span style={{ ...numSx, fontSize:numFontSize }}>{kpis.uniquePros ?? 0}</span>}
            {!loading && !isMobile && kpis.newProsThisMonth > 0 && (
              <span style={trendSx(true)}>+{kpis.newProsThisMonth}</span>
            )}
          </div>
        </div>
        <div style={iconBoxSx(T.greenTint)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.greenDark} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
      </div>
      {prosList.length > 0 && (
        <div style={{ display:"flex", alignItems:"center", marginBottom:12 }}>
          {prosList.slice(0, isMobile ? 4 : 6).map((p, i) => (
            <div key={i} style={{
              width:24, height:24, borderRadius:"50%",
              background: p.color || T.green,
              border:"2px solid #fff", marginLeft: i > 0 ? -7 : 0,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:7, fontWeight:700, color:"#fff", fontFamily:T.sans,
              zIndex: prosList.length - i, position:"relative",
            }}>{p.initials}</div>
          ))}
          {prosList.length > 0 && <span style={{ marginLeft:9, fontSize:10, color:T.faint, fontFamily:T.sans }}>{cats.length} categories</span>}
        </div>
      )}
      {cats.length > 0 ? cats.slice(0, 3).map((c, ci) => (
        <div key={c.label} style={{ marginBottom:7 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
            <span style={{ fontSize:11, color:T.sub, fontFamily:T.sans }}>{c.label}</span>
            <span style={{ fontSize:11, fontWeight:600, color:T.text, fontFamily:T.sans }}>{c.n}</span>
          </div>
          <div style={{ height:4, borderRadius:T.rFull, background:T.borderFaint, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${Math.round((c.n / catMax) * 100)}%`, borderRadius:T.rFull, background:catColors[ci] || T.green, opacity:0.8 }}/>
          </div>
        </div>
      )) : (
        <p style={{ margin:0, fontSize:11, color:T.faint, fontFamily:T.sans }}>{loading ? "Loading..." : "Hire your first pro to see categories"}</p>
      )}
    </div>
  );
}

function StatRating({ kpis, loading }) {
  const [h, setH]  = useState(false);
  const { isMobile } = useBreakpoint();
  const cardPadding  = isMobile ? "14px 16px" : "20px 22px";
  const numFontSize  = isMobile ? 22 : 26;
  const avg          = kpis.avgRating ?? 0;
  const breakdown    = kpis.ratingBreakdown || [];
  const bMax         = Math.max(...breakdown.map(r => r.n), 1);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ ...cardSx(h, isMobile), padding:cardPadding }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
        <div>
          <p style={labelSx}>Avg rating</p>
          <div style={{ display:"flex", alignItems:"baseline", gap:6, marginTop:4 }}>
            {loading ? <KpiSkeleton/> : <span style={{ ...numSx, fontSize:numFontSize }}>{avg > 0 ? avg.toFixed(1) : "—"}</span>}
            {avg > 0 && <span style={{ fontSize:12, color:T.faint, fontFamily:T.sans }}>/5</span>}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4 }}>
            <Stars n={Math.round(avg)} size={11}/>
            <span style={{ fontSize:10, color:T.faint, fontFamily:T.sans }}>{kpis.reviewCount ?? 0} review{kpis.reviewCount !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div style={iconBoxSx(T.amberTint)}>
          <svg width="15" height="15" viewBox="0 0 20 20" fill="#D97706">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </div>
      </div>
      {breakdown.length > 0 ? (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {breakdown.map(r => (
            <div key={r.s} style={{ display:"flex", alignItems:"center", gap:7 }}>
              <span style={{ fontSize:10, color:T.faint, fontFamily:T.sans, width:8, textAlign:"right", flexShrink:0 }}>{r.s}</span>
              <svg width="8" height="8" viewBox="0 0 20 20" fill="#D97706" style={{ flexShrink:0 }}>
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <div style={{ flex:1, height:5, borderRadius:T.rFull, background:T.borderFaint, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${Math.round((r.n / bMax) * 100)}%`, borderRadius:T.rFull, background:"#D97706", opacity:0.85 }}/>
              </div>
              <span style={{ fontSize:10, color:T.faint, fontFamily:T.sans, width:10, flexShrink:0 }}>{r.n}</span>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ margin:0, fontSize:11, color:T.faint, fontFamily:T.sans }}>{loading ? "Loading..." : "No reviews given yet"}</p>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   BOOKING TABS
════════════════════════════════════════════════ */
function BookingTabs({ active, onChange, counts }) {
  const { isMobile } = useBreakpoint();
  const tabs = [
    { id:"active", label:"Active jobs", short:"Active" },
    { id:"past",   label:"Past jobs",   short:"Past" },
    { id:"saved",  label:"Saved / rebook", short:"Saved" },
  ];
  return (
    <div style={{ display:"flex", alignItems:"center", background:T.card2, border:`1px solid ${T.borderFaint}`, borderRadius:T.r12, padding:4, gap:3, width: isMobile ? "100%" : "auto" }}>
      {tabs.map(t => {
        const on = active === t.id;
        return (
          <button key={t.id} onClick={() => onChange(t.id)} style={{
            padding: isMobile ? "7px 6px" : "7px 16px", borderRadius:T.r10,
            border:`1px solid ${on ? T.greenBd : "transparent"}`,
            fontSize: isMobile ? 11.5 : 13, fontWeight: on ? 600 : 400,
            background: on ? T.greenTint : "transparent",
            color: on ? T.greenDark : T.sub, cursor:"pointer", fontFamily:T.sans,
            display:"flex", alignItems:"center", justifyContent:"center", gap: isMobile ? 4 : 7,
            transition:"all 0.18s ease-out",
            minHeight: isMobile ? tokens.spacing[44] : "auto",
            flex: isMobile ? 1 : "none",
            whiteSpace: "nowrap",
          }}>
            {isMobile ? t.short : t.label}
            <span style={{
              fontSize:10, fontWeight:700, minWidth:18, height:18,
              borderRadius:T.rFull, padding:"0 5px",
              background: on ? T.green : T.border, color: on ? "#fff" : T.faint,
              display:"inline-flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.18s", flexShrink: 0,
            }}>{counts[t.id] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════
   LOADING / ERROR / EMPTY STATES
════════════════════════════════════════════════ */
function LoadingState() {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ background:T.card, borderRadius:T.r16, border:`1px solid ${T.border}`, padding: isMobile ? "48px 16px" : "72px 24px", textAlign:"center", boxShadow:T.s1 }}>
      <div style={{ width:36, height:36, borderRadius:"50%", border:`3px solid ${T.greenBd}`, borderTopColor:T.green, animation:"spin 0.7s linear infinite", margin:"0 auto 16px" }}/>
      <p style={{ margin:0, fontSize:14, color:T.faint, fontFamily:T.sans }}>Loading your bookings...</p>
    </div>
  );
}

function ErrorState({ onRetry }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ background:T.card, borderRadius:T.r16, border:`1px solid #FECACA`, padding: isMobile ? "48px 16px" : "72px 24px", textAlign:"center", boxShadow:T.s1 }}>
      <div style={{ width:46, height:46, borderRadius:"50%", background:T.redTint, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <h3 style={{ margin:"0 0 8px", fontFamily:T.serif, fontStyle:"italic", fontSize:20, fontWeight:400, color:T.text }}>Failed to load bookings</h3>
      <p style={{ margin:"0 0 24px", fontSize:13.5, color:T.sub, fontFamily:T.sans, lineHeight:1.65 }}>
        We could not connect to the server. Please check your connection and try again.
      </p>
      <Btn v="primary" size="md" onClick={onRetry}>Try again</Btn>
    </div>
  );
}

/* Empty state icons simplified to a single green badge, matching the
   restrained icon-badge language used on the landing page's benefit cards */
function EmptyIcon({ path }) {
  return (
    <div style={{ width:52, height:52, borderRadius:16, background:T.greenTint, border:`1px solid ${T.greenBd}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.greenDark} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
    </div>
  );
}

function EmptyState({ tab, onBrowse }) {
  const { isMobile } = useBreakpoint();
  const copy = {
    active: {
      h: "No bookings yet",
      b: "You have not booked a JiraniPro yet. Browse trusted professionals near you and get started today.",
      cta: "Browse services",
      icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M8 15l2.5 2.5L16 12"/></>,
      hint: "Trusted pros · Verified reviews · M-Pesa payments",
    },
    past: {
      h: "No past bookings",
      b: "Your completed and cancelled bookings will show up here once you have hired your first JiraniPro.",
      cta: "Browse services",
      icon: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
      hint: "All your history lives here after your first booking",
    },
    saved: {
      h: "No saved pros yet",
      b: "Tap the heart on any professional's profile to save them here for quick rebooking.",
      cta: "Find professionals",
      icon: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>,
      hint: "Saved pros appear here for instant rebooking",
    },
  }[tab];

  return (
    <div style={{
      background: T.card,
      borderRadius: T.r16,
      border: `1px solid ${T.border}`,
      boxShadow: T.s1,
      padding: isMobile ? "44px 24px" : "68px 40px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}>
      <div style={{ marginBottom: 20 }}><EmptyIcon path={copy.icon}/></div>
      <h3 style={{
        margin: "0 0 10px",
        fontFamily: T.serif, fontStyle: "italic",
        fontSize: isMobile ? 21 : 24,
        fontWeight: 400,
        color: T.text,
        lineHeight: 1.2,
      }}>{copy.h}</h3>
      <p style={{
        margin: "0 0 8px",
        fontSize: 13.5,
        color: T.sub,
        fontFamily: T.sans,
        lineHeight: 1.7,
        maxWidth: 340,
      }}>{copy.b}</p>
      <p style={{
        margin: "0 0 28px",
        fontSize: 10.5,
        color: T.faint,
        fontFamily: T.sans,
        letterSpacing: "0.05em",
        fontWeight: 600,
        textTransform: "uppercase",
      }}>{copy.hint}</p>
      <Btn v="primary" size="lg" onClick={onBrowse}>{copy.cta}</Btn>
    </div>
  );
}

/* ════════════════════════════════════════════════
   ACTIVE BOOKING CARD — rebuilt for mobile.
   Desktop keeps the single row. Mobile switches to
   a clear stacked layout instead of a wrapped row
   of fixed-width cells.
════════════════════════════════════════════════ */
function ActiveCard({ b, navigate, onMessage }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <div style={{
        background:T.card, borderRadius:T.r16, border:`1px solid ${T.border}`,
        padding:"16px", boxShadow:T.s1,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
          <Ava pro={b.pro} size={44}/>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ margin:0, fontSize:14, fontWeight:600, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.pro.name}</p>
            <p style={{ margin:"2px 0 0", fontSize:12, color:T.sub, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.pro.service}</p>
          </div>
          <Pill status={b.status}/>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"12px 0", borderTop:`1px solid ${T.borderFaint}`, borderBottom:`1px solid ${T.borderFaint}`, marginBottom:14 }}>
          <div>
            <p style={{ margin:0, fontSize:10, fontWeight:600, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.06em" }}>When</p>
            <p style={{ margin:"3px 0 0", fontSize:12.5, fontWeight:600, color:T.text, fontFamily:T.sans }}>{b.date} · {b.time}</p>
          </div>
          <div>
            <p style={{ margin:0, fontSize:10, fontWeight:600, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.06em" }}>Where</p>
            <p style={{ margin:"3px 0 0", fontSize:12.5, fontWeight:600, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.location}</p>
          </div>
        </div>

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <span style={{ fontSize:10, fontWeight:600, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.06em" }}>Price</span>
          <span style={{ fontSize:18, fontWeight:700, color:T.text, fontFamily:T.sans, letterSpacing:"-0.03em" }}>Ksh {b.price.toLocaleString()}</span>
        </div>

        <div style={{ display:"flex", gap:8 }}>
          <Btn v="ghost" size="sm" full onClick={() => navigate(`/bookings/${b.id}/track`)}>Track order</Btn>
          <Btn v="dark" size="sm" full onClick={() => onMessage(b)}>Message</Btn>
        </div>
      </div>
    );
  }

  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      background:T.card, borderRadius:T.r16,
      border:`1px solid ${h ? T.greenBd : T.border}`,
      padding:"18px 24px",
      display:"flex", alignItems:"center", gap:20,
      boxShadow: h ? T.s3 : T.s1,
      transition:"all 0.2s ease-out",
    }}>
      <Ava pro={b.pro} size={52}/>

      <div style={{ minWidth:160, flexShrink:0 }}>
        <p style={{ margin:0, fontSize:14, fontWeight:600, color:T.text, fontFamily:T.sans }}>{b.pro.name}</p>
        <p style={{ margin:"3px 0 0", fontSize:12, fontWeight:400, color:T.sub, fontFamily:T.sans, display:"flex", alignItems:"center", gap:4 }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.faint} strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          {b.pro.service}
        </p>
      </div>

      <MetaCell icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>} label="Calendar" primary={b.date} secondary={b.time}/>
      <MetaCell flex icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>} label="Location" primary={b.location}/>

      <div style={{ minWidth:110, flexShrink:0 }}>
        <p style={{ margin:0, fontSize:10, fontWeight:600, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.06em" }}>Price</p>
        <p style={{ margin:"3px 0 0", fontSize:20, fontWeight:700, color:T.text, fontFamily:T.sans, letterSpacing:"-0.03em" }}>Ksh {b.price.toLocaleString()}</p>
      </div>

      <Pill status={b.status}/>

      <div style={{ display:"flex", gap:8, flexShrink:0, marginLeft:"auto" }}>
        <Btn v="ghost" size="sm" onClick={() => navigate(`/bookings/${b.id}/track`)}>Track order</Btn>
        <Btn v="dark" size="sm" onClick={() => onMessage(b)}>Message</Btn>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   PAST BOOKING CARD — same stacked treatment on mobile
════════════════════════════════════════════════ */
function PastCard({ b, navigate, onMessage }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <div style={{ background:T.card, borderRadius:T.r16, border:`1px solid ${T.border}`, boxShadow:T.s1, padding:"14px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
          <Ava pro={b.pro} size={40}/>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ margin:0, fontSize:13.5, fontWeight:600, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.pro.name}</p>
            <p style={{ margin:"2px 0 0", fontSize:11.5, color:T.faint, fontFamily:T.sans }}>{b.date} · {b.location}</p>
          </div>
          <Pill status={b.status}/>
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <span style={{ fontSize:12, color:T.sub, fontFamily:T.sans }}>{b.pro.service}</span>
          <span style={{ fontSize:15, fontWeight:700, color:T.text, fontFamily:T.sans, letterSpacing:"-0.02em" }}>Ksh {b.price.toLocaleString()}</span>
        </div>
        {b.status === "Completed" && b.reviewed && <div style={{ marginBottom:12 }}><Stars n={b.rating}/></div>}
        <div style={{ display:"flex", gap:8 }}>
          <Btn v="ghost" size="sm" full onClick={() => navigate(`/bookings/${b.id}`)}>Details</Btn>
          {b.status === "Completed" && !b.reviewed && (
            <Btn v="review" size="sm" full onClick={() => navigate(`/review/${b.id}`)}>Leave a review</Btn>
          )}
          {b.status === "Completed" && (
            <Btn v="rebook" size="sm" full onClick={() => navigate(`/pro/${b.professionalId || b.id}`)}>Rebook</Btn>
          )}
        </div>
      </div>
    );
  }

  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      background:T.card, borderRadius:T.r16,
      border:`1px solid ${T.border}`,
      boxShadow: h ? T.s3 : T.s1,
      transition:"all 0.2s ease-out", overflow:"hidden",
    }}>
      <div style={{ padding:"16px 24px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
        <Ava pro={b.pro} size={46}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
            <p style={{ margin:0, fontSize:14, fontWeight:600, color:T.text, fontFamily:T.sans }}>{b.pro.name}</p>
            <span style={{ color:T.border }}>·</span>
            <p style={{ margin:0, fontSize:12, color:T.sub, fontFamily:T.sans }}>{b.pro.service}</p>
          </div>
          <p style={{ margin:"3px 0 0", fontSize:11, color:T.faint, fontFamily:T.sans }}>{b.date} · {b.location}</p>
        </div>
        <p style={{ margin:0, fontSize:16, fontWeight:700, color:T.text, fontFamily:T.sans, letterSpacing:"-0.025em", flexShrink:0 }}>Ksh {b.price.toLocaleString()}</p>
        <Pill status={b.status}/>
        {b.status === "Completed" && b.reviewed && <Stars n={b.rating}/>}
        <div style={{ display:"flex", gap:8, flexShrink:0 }}>
          <Btn v="ghost" size="sm" onClick={() => navigate(`/bookings/${b.id}`)}>Details</Btn>
          {b.status === "Completed" && !b.reviewed && (
            <Btn v="review" size="sm" onClick={() => navigate(`/review/${b.id}`)}>Leave a review</Btn>
          )}
          {b.status === "Completed" && (
            <Btn v="rebook" size="sm" onClick={() => navigate(`/pro/${b.professionalId || b.id}`)}>Rebook</Btn>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   SAVED PRO CARD
════════════════════════════════════════════════ */
function SavedCard({ item: p, navigate, onUnsave }) {
  const [saved, setSaved] = useState(true);
  const [h, setH]         = useState(false);
  const [saving, setSaving] = useState(false);
  const { isMobile } = useBreakpoint();

  const handleSaveToggle = async () => {
    setSaving(true);
    try {
      if (saved) {
        const { error } = await supabase.from("favorites").delete().eq("id", p.favoriteId);
        if (error) throw error;
        setSaved(false);
        if (onUnsave) onUnsave(p.favoriteId);
      }
    } catch (err) {
      console.error("Unsave failed:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!saved) return null;

  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      background:T.card, borderRadius:T.r16,
      border:`1px solid ${h ? T.greenBd : T.border}`,
      overflow:"hidden", boxShadow: h ? T.s3 : T.s1,
      transition:"all 0.2s ease-out",
    }}>
      <div style={{ height:52, background:T.greenTint, position:"relative" }}>
        <button onClick={handleSaveToggle} disabled={saving} style={{
          position:"absolute", top:10, right:10, width:28, height:28,
          borderRadius:"50%", background:T.white, border:`1px solid ${T.borderFaint}`,
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:T.s1, minHeight: isMobile ? tokens.spacing[44] : "auto",
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#EF4444" stroke="#EF4444" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
      </div>
      <div style={{ padding: isMobile ? "0 14px 16px" : "0 18px 20px" }}>
        <div style={{ marginTop:-22, marginBottom:12 }}><Ava pro={p.pro} size={44}/></div>
        <p style={{ margin:"0 0 2px", fontSize: isMobile ? 13 : 14, fontWeight:600, color:T.text, fontFamily:T.sans }}>{p.pro.name}</p>
        <p style={{ margin:"0 0 10px", fontSize:12, color:T.sub, fontFamily:T.sans }}>{p.pro.service}</p>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5, flexWrap:"wrap" }}>
          <Stars n={Math.round(p.rating)} size={11}/>
          <span style={{ fontSize:12, fontWeight:600, color:T.text, fontFamily:T.sans }}>{p.rating > 0 ? p.rating.toFixed(1) : "—"}</span>
          {p.reviews > 0 && <span style={{ fontSize:11, color:T.faint, fontFamily:T.sans }}>({p.reviews})</span>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:16 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.faint} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span style={{ fontSize:11, color:T.faint, fontFamily:T.sans }}>{p.location}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
          <div>
            {p.price > 0 && <>
              <span style={{ fontSize:11, color:T.faint, fontFamily:T.sans }}>From </span>
              <span style={{ fontSize: isMobile ? 14 : 15, fontWeight:700, color:T.text, fontFamily:T.sans, letterSpacing:"-0.02em" }}>Ksh {p.price.toLocaleString()}</span>
            </>}
          </div>
          <Btn v="primary" size="sm" onClick={() => navigate(`/pro/${p.id}`)}>Book now</Btn>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   NEXT BOOKING STRIP
════════════════════════════════════════════════ */
function NextBookingStrip({ b, navigate, onMessage }) {
  const { isMobile } = useBreakpoint();
  if (!b) return null;
  return (
    <div style={{
      background:T.greenTint, border:`1px solid ${T.greenBd}`,
      borderRadius:T.r14, padding: isMobile ? "12px 16px" : "14px 22px",
      display:"flex", alignItems:"center",
      justifyContent:"space-between",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 12 : 0,
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ width:38, height:38, borderRadius:T.r10, background:T.green, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        </div>
        <div>
          <p style={{ margin:0, fontSize:13, fontWeight:600, color:T.greenDark, fontFamily:T.sans }}>
            Next: {b.date} at {b.time}
          </p>
          <p style={{ margin:"2px 0 0", fontSize:12, color:T.sub, fontFamily:T.sans }}>
            {b.pro.name} · {b.pro.service} · {b.location}
          </p>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, flexShrink:0 }}>
        <Btn v="ghost" size="sm" onClick={() => onMessage(b)}>Message</Btn>
        <Btn v="primary" size="sm" onClick={() => navigate(`/bookings/${b.id}/track`)}>Track order</Btn>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   SPENDING OVERVIEW
════════════════════════════════════════════════ */
function SpendingOverview({ kpis }) {
  const { isMobile } = useBreakpoint();
  const thisMonth  = kpis.thisMonthSpend   ?? 0;
  const lastMonth  = kpis.lastMonthSpend   ?? 0;
  const allTime    = kpis.totalSpend       ?? 0;
  const thisMoCount = kpis.thisMonthCount  ?? 0;
  const lastMoCount = kpis.lastMonthCount  ?? 0;
  const allCount    = kpis.totalBookings   ?? 0;
  const monthlyDiff = lastMonth > 0 ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100) : null;
  const cardPadding = isMobile ? "16px 20px" : "22px 26px";
  const items = [
    { lbl:"This month",  val:`Ksh ${thisMonth.toLocaleString()}`,  sub:`${thisMoCount} booking${thisMoCount !== 1 ? "s" : ""}`, accent:T.green,   trend: monthlyDiff != null ? `${monthlyDiff >= 0 ? "+" : ""}${monthlyDiff}%` : null, up: monthlyDiff != null ? monthlyDiff >= 0 : null },
    { lbl:"Last month",  val:`Ksh ${lastMonth.toLocaleString()}`,  sub:`${lastMoCount} booking${lastMoCount !== 1 ? "s" : ""}`, accent:"#0369A1", trend: null, up: null },
    { lbl:"All time",    val:`Ksh ${allTime.toLocaleString()}`,    sub:`${allCount} booking${allCount !== 1 ? "s" : ""}`,        accent:"#7C3AED", trend: null, up: null },
  ];
  return (
    <div style={{ background:T.card, borderRadius:T.r16, border:`1px solid ${T.border}`, padding:cardPadding, boxShadow:T.s1 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, flexWrap:"wrap", gap:12 }}>
        <div>
          <p style={{ margin:0, fontSize: isMobile ? 14 : 15, fontWeight:600, color:T.text, fontFamily:T.sans }}>Spending overview</p>
          <p style={{ margin:"3px 0 0", fontSize:12, color:T.faint, fontFamily:T.sans }}>Across all bookings</p>
        </div>
        {!isMobile && monthlyDiff != null && (
          <div style={{ display:"flex", alignItems:"center", gap:4, background:T.greenTint, border:`1px solid ${T.greenBd}`, borderRadius:T.rFull, padding:"4px 10px" }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.greenDark} strokeWidth="2.5"><path d={monthlyDiff >= 0 ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}/></svg>
            <span style={{ fontSize:11, fontWeight:600, color:T.greenDark, fontFamily:T.sans }}>{monthlyDiff >= 0 ? "+" : ""}{monthlyDiff}% vs last month</span>
          </div>
        )}
      </div>
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap:14 }}>
        {items.map(s => (
          <div key={s.lbl} style={{ background:T.card2, borderRadius:T.r12, padding:"16px 18px", border:`1px solid ${T.borderFaint}`, borderTop:`3px solid ${s.accent}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8, flexWrap:"wrap", gap:4 }}>
              <p style={{ margin:0, fontSize:10, fontWeight:500, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.08em" }}>{s.lbl}</p>
              {s.trend && <span style={{ fontSize:10, fontWeight:600, color: s.up ? T.greenDark : T.red, background: s.up ? T.greenTint : T.redTint, borderRadius:T.rFull, padding:"2px 6px", fontFamily:T.sans }}>{s.up ? "+" : ""}{s.trend}</span>}
            </div>
            <p style={{ margin:"0 0 3px", fontSize: isMobile ? 18 : 20, fontWeight:700, color:T.text, fontFamily:T.sans, letterSpacing:"-0.03em", lineHeight:1 }}>{s.val}</p>
            <p style={{ margin:0, fontSize:12, color:T.sub, fontFamily:T.sans }}>{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   DISCOVER STRIP — now the true JiraniApp dark
   green surface with ambient glow, not near-black
════════════════════════════════════════════════ */
function DiscoverStrip({ onBrowse }) {
  const { isMobile } = useBreakpoint();
  const prev = [
    { initials:"SM", color:T.green }, { initials:"DK", color:"#0369A1" },
    { initials:"AW", color:"#7C3AED" }, { initials:"JO", color:T.amber },
    { initials:"MW", color:T.greenDark },
  ];
  return (
    <div style={{ borderRadius:T.r16, overflow:"hidden", position:"relative", background:T.deepGreen, boxShadow:T.s2 }}>
      <div style={{ position:"absolute", top:-90, right:80, width:280, height:280, borderRadius:"50%", background:"radial-gradient(circle, rgba(30,146,84,0.16) 0%, transparent 70%)", pointerEvents:"none" }}/>
      <div style={{ position:"relative", zIndex:2, padding: isMobile ? "22px 22px" : "28px 44px", display:"flex", alignItems:"center", justifyContent:"space-between", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 20 : 0 }}>
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          <p style={{ margin:0, fontSize:10.5, fontWeight:700, color:T.greenLight, textTransform:"uppercase", letterSpacing:"0.13em", fontFamily:T.sans }}>Ready to book</p>
          <p style={{ margin:0, fontFamily:T.serif, fontStyle:"italic", fontSize: isMobile ? 19 : 23, fontWeight:400, color:"#fff", letterSpacing:"-0.01em" }}>Find your next JiraniPro</p>
          {!isMobile && <p style={{ margin:0, fontSize:12.5, color:"rgba(255,255,255,0.5)", fontFamily:T.sans }}>2,400+ verified professionals across Nairobi</p>}
          <div style={{ display:"flex", alignItems:"center", gap:0, marginTop:8 }}>
            {prev.slice(0, isMobile ? 3 : 5).map((p, i) => (
              <div key={i} style={{ width:26, height:26, borderRadius:"50%", background:p.color, border:`2px solid ${T.deepGreen}`, marginLeft: i > 0 ? -7 : 0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:700, color:"#fff", fontFamily:T.sans, zIndex:prev.length - i, position:"relative" }}>{p.initials}</div>
            ))}
            <span style={{ marginLeft:10, fontSize:11.5, color:"rgba(255,255,255,0.5)", fontFamily:T.sans }}>+ 2,395 more</span>
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems: isMobile ? "stretch" : "flex-end", gap:10 }}>
          <Btn v="primary" size={isMobile ? "sm" : "lg"} full={isMobile} onClick={onBrowse}>Browse services</Btn>
          {!isMobile && <p style={{ margin:0, fontSize:11, color:"rgba(255,255,255,0.32)", fontFamily:T.sans }}>Westlands · Kilimani · Karen · Lavington</p>}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   BOTTOM TAB BAR — the core mobile navigation.
   Replaces the desktop-only top bar pattern with the
   fixed 5-tab bar mobile apps actually use.
════════════════════════════════════════════════ */
const NAV_ICONS = {
  home:     <><path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9"/></>,
  search:   <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></>,
  bookings: <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
  messages: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  profile:  <><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></>,
};

function BottomNav({ activeId, onNav, unreadCount }) {
  // ⚠️  Adjust "profile" path if your app uses a different route for the account/profile screen.
  const tabs = [
    { id:"home",     label:"Home",     path:"/" },
    { id:"search",   label:"Search",   path:"/search" },
    { id:"profile",  label:"Profile",  path:"/profile" },
  ];
  return (
    <nav style={{
      position:"fixed", bottom:0, left:0, right:0, zIndex:250,
      background:"rgba(255,255,255,0.98)",
      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
      borderTop:`1px solid ${T.borderFaint}`,
      display:"flex", justifyContent:"space-around", alignItems:"center",
      padding:"8px 4px calc(6px + env(safe-area-inset-bottom))",
    }}>
      {tabs.map(t => {
        const on = activeId === t.id;
        return (
          <button key={t.id} onClick={() => onNav(t.path)} style={{
            background:"none", border:"none", cursor:"pointer",
            display:"flex", flexDirection:"column", alignItems:"center", gap:3,
            padding:"4px 6px", minWidth: tokens.spacing[44], minHeight: tokens.spacing[44],
            color: on ? T.greenDark : T.faint, fontFamily:T.sans,
          }}>
            <span style={{ position:"relative", display:"inline-flex" }}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? 2.1 : 1.8}>
                {NAV_ICONS[t.id]}
              </svg>
            </span>
            <span style={{ fontSize:10, fontWeight: on ? 700 : 500 }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ════════════════════════════════════════════════
   HELPFUL LINKS — same "From JiraniApp" trust-card +
   quick-links pattern used on the notifications page,
   re-themed to this file's own tokens. Shown as one
   section below the bookings list on every breakpoint,
   not a persistent side column — say the word if you'd
   rather have it as a true right-hand sidebar like the
   notifications page instead.
════════════════════════════════════════════════ */
const HELPFUL_CARDS = [
  { img: "/images/jirani-guarantee.png", eyebrow: "Your protection", heading: "The JiraniApp Guarantee", desc: "Every booking is backed by our guarantee. If something goes wrong, we make it right.", link: "/guarantee", linkLabel: "Learn how we protect you" },
  { img: "/images/verified-pros.png", eyebrow: "Who you're booking", heading: "Verified JiraniPros only", desc: "Every pro on the platform is identity-verified and reviewed by real clients.", link: "/find-a-jiranipro", linkLabel: "Browse verified pros" },
  { img: "/images/mpesa-safe.png", eyebrow: "Safe payments", heading: "Pay securely with M-Pesa", desc: "Payment is held safely and only released once your job is done and confirmed.", link: "/how-it-works", linkLabel: "How payments work" },
];
const HELPFUL_QUICK_LINKS = [
  { label: "How JiraniApp works", path: "/how-it-works" },
  { label: "Trust & Safety",       path: "/trust-and-safety" },
  { label: "Help & Support",        path: "/help" },
  { label: "Community Hub",         path: "/community-hub" },
  { label: "Browse by location",    path: "/browse-by-location" },
];

function HelpfulCard({ card, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onClick={() => navigate(card.link)}
      style={{ borderRadius: T.r16, overflow: "hidden", border: `1.5px solid ${hovered ? T.greenBd : T.border}`, background: T.card, cursor: "pointer", transition: T.tx, boxShadow: hovered ? "0 8px 28px rgba(30,146,84,0.10)" : T.s1 }}>
      <div style={{ position: "relative", width: "100%", height: 118, overflow: "hidden", background: "#E5E7EB" }}>
        <img src={card.img} alt={card.heading} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={e => { e.currentTarget.style.display = "none"; }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 52, background: "linear-gradient(to top, rgba(255,255,255,0.97) 0%, transparent 100%)", pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "12px 16px 16px" }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans, margin: "0 0 4px" }}>{card.eyebrow}</p>
        <p style={{ fontFamily: T.serif, fontSize: 15, fontWeight: 400, color: T.text, lineHeight: 1.3, letterSpacing: "-0.02em", margin: "0 0 6px", fontStyle: "italic" }}>{card.heading}</p>
        <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.6, margin: "0 0 10px", fontFamily: T.sans }}>{card.desc}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: T.green, fontFamily: T.sans }}>{card.linkLabel} →</div>
      </div>
    </div>
  );
}

function HelpfulQuickLink({ link, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onClick={() => navigate(link.path)}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.borderFaint}`, cursor: "pointer" }}>
      <span style={{ fontSize: 13, color: hovered ? T.green : T.textMid, fontFamily: T.sans, transition: T.tx }}>{link.label}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={hovered ? T.green : T.ghost} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
    </div>
  );
}

function HelpfulLinks({ navigate }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ marginTop: 8 }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 12px" }}>From JiraniApp</p>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
        {HELPFUL_CARDS.map((card, i) => <HelpfulCard key={i} card={card} navigate={navigate} />)}
      </div>
      <div style={{ padding: "18px 16px", borderRadius: T.r14, border: `1px solid ${T.border}`, background: T.card }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 12px" }}>Useful links</p>
        {HELPFUL_QUICK_LINKS.map((link, i) => <HelpfulQuickLink key={i} link={link} navigate={navigate} />)}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   PAGE SHELL
════════════════════════════════════════════════ */
export default function BookingsPage() {
  const navigate    = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { user, logoutUser } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();

  const [tab, setTab]               = useState("active");
  const [activeList,   setActiveList]   = useState([]);
  const [pastList,     setPastList]     = useState([]);
  const [savedList,    setSavedList]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [savedLoading, setSavedLoading] = useState(true);
  const [savedError,   setSavedError]   = useState(null);
  const [kpis,       setKpis]       = useState({});
  const [kpiLoading, setKpiLoading] = useState(true);
  const [unreadMsgCount,   setUnreadMsgCount]   = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  useEffect(() => {
    if (!user) openLogin();
  }, [user, navigate]);

  const fetchBookings = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("job_requests")
        .select(`
          id, status, scheduled_time, location, estimated_price, description, pro_id,
          pro_profiles ( id, profiles!user_id ( full_name, location ) ),
          reviews ( rating, comment )
        `)
        .eq("user_id", user.id)
        .order("scheduled_time", { ascending: false });

      if (err) throw err;

      const mapped = (data || []).map(mapBooking);
      const active = mapped.filter(b => b.status === "Upcoming" || b.status === "In Progress");
      const past   = mapped.filter(b => b.status === "Completed" || b.status === "Cancelled");

      setActiveList(active);
      setPastList(past);
    } catch (err) {
      console.error("Bookings fetch failed:", err);
      setError(err.message || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchKPIs = useCallback(async () => {
    if (!user?.id) return;
    setKpiLoading(true);
    try {
      const { data: allBookings, error: bErr } = await supabase
        .from("job_requests")
        .select("id, status, scheduled_time, estimated_price, description, pro_id, pro_profiles ( id, profiles!user_id ( full_name ) )")
        .eq("user_id", user.id);
      if (bErr) throw bErr;

      const totalBookings = allBookings?.length ?? 0;
      const totalSpend = (allBookings || [])
        .filter(b => b.status === "completed")
        .reduce((sum, b) => sum + (b.estimated_price || 0), 0);

      const proIds = [...new Set((allBookings || []).map(b => b.pro_id).filter(Boolean))];
      const uniquePros = proIds.length;

      const prosDetail = proIds.slice(0, 8).map(id => {
        const b = allBookings.find(x => x.pro_id === id);
        const name = b?.pro_profiles?.profiles?.full_name || "Pro";
        const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
        const colors = [T.green, "#0369A1", "#7C3AED", T.amber, "#BE185D", T.textMid, "#0E7490", T.greenDark];
        const color = colors[Math.abs(name.charCodeAt(0)) % colors.length];
        return { id, initials, color };
      });

      const catMap = {};
      (allBookings || []).forEach(b => {
        const cat = b.description || "Other";
        catMap[cat] = (catMap[cat] || 0) + 1;
      });
      const prosCategories = Object.entries(catMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([label, n]) => ({ label, n }));

      const monthlyMap = {};
      (allBookings || []).forEach(b => {
        const month = (b.scheduled_time || "").slice(0, 7);
        if (!month) return;
        monthlyMap[month] = (monthlyMap[month] || 0) + 1;
      });
      const monthlyBookings = Object.entries(monthlyMap)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-7)
        .map(([month, count]) => ({ month, count }));

      const spendMap = {};
      (allBookings || []).filter(b => b.status === "completed").forEach(b => {
        const month = (b.scheduled_time || "").slice(0, 7);
        if (!month) return;
        spendMap[month] = (spendMap[month] || 0) + (b.estimated_price || 0);
      });
      const monthlySpend = Object.entries(spendMap)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-4)
        .map(([month, total]) => ({ month, total }));

      const now      = new Date();
      const thisMo   = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const lastMoDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMo   = `${lastMoDate.getFullYear()}-${String(lastMoDate.getMonth() + 1).padStart(2, "0")}`;

      const thisMonthBookings = (allBookings || []).filter(b => (b.scheduled_time || "").startsWith(thisMo));
      const lastMonthBookings = (allBookings || []).filter(b => (b.scheduled_time || "").startsWith(lastMo));
      const thisMonthSpend    = thisMonthBookings.filter(b => b.status === "completed").reduce((s, b) => s + (b.estimated_price || 0), 0);
      const lastMonthSpend    = lastMonthBookings.filter(b => b.status === "completed").reduce((s, b) => s + (b.estimated_price || 0), 0);
      const bookingTrend      = thisMonthBookings.length - lastMonthBookings.length;

      const thisMoProIds   = [...new Set(thisMonthBookings.map(b => b.pro_id).filter(Boolean))];
      const lastMoProIds   = [...new Set(lastMonthBookings.map(b => b.pro_id).filter(Boolean))];
      const newProsThisMonth = thisMoProIds.filter(id => !lastMoProIds.includes(id)).length;

      const { data: reviews, error: rErr } = await supabase
        .from("reviews")
        .select("rating")
        .in("job_request_id", (allBookings || []).map(b => b.id));
      if (rErr) throw rErr;

      const reviewCount    = reviews?.length ?? 0;
      const avgRating      = reviewCount > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviewCount) : 0;

      const rbMap = { 5:0, 4:0, 3:0, 2:0, 1:0 };
      (reviews || []).forEach(r => { if (rbMap[r.rating] !== undefined) rbMap[r.rating]++; });
      const ratingBreakdown = [5,4,3].map(s => ({ s, n: rbMap[s] || 0 })).filter(r => r.n > 0);

      setKpis({
        totalBookings,   totalSpend,     uniquePros,
        avgRating,       reviewCount,    ratingBreakdown,
        monthlyBookings, monthlySpend,
        thisMonthSpend,  lastMonthSpend,
        thisMonthCount:  thisMonthBookings.length,
        lastMonthCount:  lastMonthBookings.length,
        bookingTrend,    newProsThisMonth,
        prosDetail,      prosCategories,
        spendBudget:     0,
      });
    } catch (err) {
      console.error("KPI fetch failed:", err);
    } finally {
      setKpiLoading(false);
    }
  }, [user?.id]);

  const fetchSaved = useCallback(async () => {
    if (!user?.id) return;
    setSavedLoading(true);
    setSavedError(null);
    try {
      const { data, error: err } = await supabase
        .from("favorites")
        .select(`
          id, professional_id,
          pro_profiles (
            id,
            profiles!user_id ( full_name, location ),
            services ( name ),
            avg_rating, review_count, base_price
          )
        `)
        .eq("customer_id", user.id)
        .order("created_at", { ascending: false });

      if (err) throw err;
      setSavedList((data || []).map(mapSavedPro));
    } catch (err) {
      console.error("Saved pros fetch failed:", err);
      setSavedError(err.message || "Failed to load saved pros");
    } finally {
      setSavedLoading(false);
    }
  }, [user?.id]);

  const fetchUnreadCounts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { count: msgCount } = await supabase
        .from("chat_messages")
        .select("id", { count:"exact", head:true })
        .neq("status", "read")
        .neq("sender_id", user.id);

      const { count: notifCount } = await supabase
        .from("notifications")
        .select("id", { count:"exact", head:true })
        .eq("user_id", user.id)
        .eq("read", false);

      setUnreadMsgCount(msgCount ?? 0);
      setUnreadNotifCount(notifCount ?? 0);
    } catch (err) {
      console.error("Unread count fetch failed:", err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBookings();
    fetchKPIs();
    fetchSaved();
    fetchUnreadCounts();
  }, [fetchBookings, fetchKPIs, fetchSaved, fetchUnreadCounts]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${user.id}`,
      }, () => {
        setUnreadNotifCount(c => c + 1);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`job-requests-${user.id}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "job_requests",
        filter: `user_id=eq.${user.id}`,
      }, () => {
        fetchBookings();
        fetchKPIs();
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.id, fetchBookings, fetchKPIs]);

  const handleMessage = useCallback(async (booking) => {
    if (!booking.professionalId) {
      navigate("/messages");
      return;
    }
    try {
      const { data: existing } = await supabase
        .from("conversations")
        .select("id")
        .eq("customer_id", user.id)
        .eq("professional_id", booking.professionalId)
        .maybeSingle();

      if (existing?.id) {
        navigate(`/messages/${existing.id}`);
        return;
      }

      const { data: newConv, error: createErr } = await supabase
        .from("conversations")
        .insert({
          job_request_id:  booking.id,
          customer_id:     user.id,
          professional_id: booking.professionalId,
        })
        .select("id")
        .single();

      if (createErr) throw createErr;

      await supabase.from("conversation_participants").insert([
        { conversation_id: newConv.id, user_id: user.id },
        { conversation_id: newConv.id, user_id: booking.professionalId },
      ]);

      navigate(`/messages/${newConv.id}`);
    } catch (err) {
      console.error("Message conversation error:", err);
      navigate("/messages");
    }
  }, [navigate, user?.id]);

  const handleLogout = useCallback(() => {
    logoutUser();
    navigate("/");
  }, [logoutUser, navigate]);

  const handleUnsave = useCallback((favoriteId) => {
    setSavedList(prev => prev.filter(p => p.favoriteId !== favoriteId));
  }, []);

  const nextBooking = activeList
    .filter(b => b.status === "Upcoming" || b.status === "In Progress")
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;

  const counts = {
    active: activeList.length,
    past:   pastList.length,
    saved:  savedList.length,
  };

  const mainPadding   = isMobile ? "18px 16px calc(84px + env(safe-area-inset-bottom))" : (isTablet ? "24px 20px" : "28px 24px");
  const savedGridCols = isMobile ? "repeat(2,1fr)" : (isTablet ? "repeat(3,1fr)" : "repeat(4,1fr)");

  return (
    <div style={{ minHeight:"100vh", background:T.white, fontFamily:T.sans }}>

      <Navbar
        onNav={navigate}
        user={user}
        unreadCount={unreadMsgCount}
        notifCount={unreadNotifCount}
        onLogout={handleLogout}
      />

      <main style={{ maxWidth:1160, margin:"0 auto", padding:mainPadding, display:"flex", flexDirection:"column", gap:20 }}>

        <PageHeader
          onBrowse={() => navigate("/search")}
          user={user}
          kpis={kpis}
        />

        <StatsGrid kpis={kpis} kpiLoading={kpiLoading}/>

        <div>
          <div style={{
            display:"flex", alignItems: isMobile ? "flex-start" : "center", justifyContent:"space-between",
            marginBottom:12,
            flexDirection: isMobile ? "column" : "row", gap: isMobile ? 4 : 0,
          }}>
            <div>
              <h2 style={{ margin:0, fontFamily:T.serif, fontStyle:"italic", fontSize: isMobile ? 18 : 20, fontWeight:400, color:T.text }}>My bookings</h2>
              {!isMobile && (
                <p style={{ margin:"3px 0 0", fontSize:12, color:T.faint, fontFamily:T.sans }}>
                  {tab === "active" ? `${activeList.length} upcoming & active`
                  : tab === "past"  ? `${pastList.length} past bookings`
                  : `${savedList.length} saved professionals`}
                </p>
              )}
            </div>
          </div>

          <div style={{
            background:T.card, borderRadius:T.r16, border:`1px solid ${T.border}`, boxShadow:T.s1,
            padding: isMobile ? "8px" : "10px 14px",
            marginBottom:14,
          }}>
            <BookingTabs active={tab} onChange={setTab} counts={counts}/>
          </div>

          {tab === "active" && (
            loading ? <LoadingState/> :
            error   ? <ErrorState onRetry={fetchBookings}/> :
            activeList.length === 0 ? <EmptyState tab="active" onBrowse={() => navigate("/search")}/> :
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {activeList.map(b => (
                <ActiveCard key={b.id} b={b} navigate={navigate} onMessage={handleMessage}/>
              ))}
              {nextBooking && <NextBookingStrip b={nextBooking} navigate={navigate} onMessage={handleMessage}/>}
            </div>
          )}

          {tab === "past" && (
            loading ? <LoadingState/> :
            error   ? <ErrorState onRetry={fetchBookings}/> :
            pastList.length === 0 ? <EmptyState tab="past" onBrowse={() => navigate("/search")}/> :
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {pastList.map(b => (
                <PastCard
                  key={b.id} b={b}
                  navigate={navigate}
                  onMessage={handleMessage}
                />
              ))}
              <SpendingOverview kpis={kpis}/>
            </div>
          )}

          {tab === "saved" && (
            savedLoading ? <LoadingState/> :
            savedError   ? <ErrorState onRetry={fetchSaved}/> :
            savedList.length === 0 ? <EmptyState tab="saved" onBrowse={() => navigate("/search")}/> :
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <div style={{ display:"grid", gridTemplateColumns:savedGridCols, gap: isMobile ? 12 : 16 }}>
                {savedList.map(p => (
                  <SavedCard
                    key={p.id}
                    item={p}
                    navigate={navigate}
                    onUnsave={handleUnsave}
                  />
                ))}
              </div>
              <DiscoverStrip onBrowse={() => navigate("/search")}/>
            </div>
          )}
        </div>

        <HelpfulLinks navigate={navigate} />
      </main>

      {isMobile && (
        <BottomNav activeId="bookings" onNav={navigate} unreadCount={unreadMsgCount}/>
      )}

      <style>{`
        * { box-sizing: border-box; }
        button:focus-visible { outline: 2px solid ${T.green}; outline-offset: 2px; }
        input::placeholder { color: ${T.faint}; font-weight: 400; }
        input:focus { outline: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes kpiPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}